import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import tinycolor from 'tinycolor2';

import * as AppPropTypes from '../../lib/PropTypes';
import Slider from '../partials/Slider';

import styles from '../../../styles/blocks/cards.module.scss';
import LinkButton from '../buttons/LinkButton';
import { useIntersectionObserver } from '../../hooks/useObserver';

const propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    textColor: PropTypes.string,
    link: PropTypes.shape({
        external: PropTypes.bool,
        url: PropTypes.string,
        label: PropTypes.string,
    }),
    linkColor: PropTypes.string,
    cards: AppPropTypes.cards,
    className: PropTypes.string,
    cardsClassName: PropTypes.string,
    sliderClassName: PropTypes.string,
};

const defaultProps = {
    title: false,
    description: null,
    textColor: null,
    link: null,
    linkColor: null,
    cards: null,
    className: null,
    cardsClassName: null,
    sliderClassName: null,
};

const CardsBlock = ({
    title,
    description,
    textColor,
    link,
    linkColor,
    cards,
    className,
    cardsClassName,
    sliderClassName,
}) => {
    const { external: externalLink, url: linkUrl, label: linkLabel } = link || {};

    const linkTextColor = useMemo(
        () => (linkColor !== null && tinycolor(linkColor).isDark() ? '#FFF' : '#000'),
        [linkColor],
    );

    const [animate, setAnimate] = useState(false);

    const {
        ref,
        entry: { isIntersecting = false },
    } = useIntersectionObserver({ rootMargin: '-30%' });

    useEffect(() => {
        if (isIntersecting) {
            setAnimate(true);
        }
    }, [isIntersecting]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
            ref={ref}
        >
            <Slider
                visible={animate}
                className={styles.slider}
                detailsClassName={styles.sliderDetails}
                textColor={textColor}
                titleClassName={styles.sliderTitle}
                descriptionClassName={styles.sliderDescription}
                title={title}
                description={description}
                cards={cards}
                cardsClassName={cardsClassName}
                cardClassName={styles.sliderCardClassName}
                sliderClassName={sliderClassName}
            />
            {link !== null && linkUrl !== null ? (
                <div className={styles.linkContainer}>
                    <LinkButton
                        className={styles.link}
                        color={linkTextColor}
                        backgroundColor={linkColor}
                        href={linkUrl}
                        external={externalLink}
                    >
                        {linkLabel !== null ? (
                            linkLabel
                        ) : (
                            <FormattedMessage defaultMessage="View {title}" values={{ title }} />
                        )}
                    </LinkButton>
                </div>
            ) : null}
        </div>
    );
};

CardsBlock.propTypes = propTypes;
CardsBlock.defaultProps = defaultProps;

export default CardsBlock;
